/* Sofortsuche (Theme „Release“), angepasst für Rheinkauf (05.10.2026)
 * - Suchbegriff wird URL-kodiert (Befund C7: &, #, + oder Umlaute führten sonst zu falschen Treffern).
 * - Läuft im Such-Drawer des Kopfes, auf der Suchseite und im großen Suchfeld
 *   (snippets/rk-suchleiste.liquid, Attribut data-rk-inline). Dort schließen Klick daneben
 *   und Esc nur die Vorschläge – der eingegebene Text bleibt stehen.
 * - Pfeiltasten führen in die Vorschläge und zurück, Esc schließt.
 * - Nur die zuletzt angefragten Treffer werden gezeigt (keine veralteten Antworten).
 * - Hinweis für Screenreader über [data-rk-ps-status] (Anzahl der Vorschläge).
 * - Artikelnummer (SKU) und EAN werden mit durchsucht.
 * - Bis zu 6 Treffer je Art (Artikel, Kategorien, Suchvorschläge, Ratgeber, Seiten),
 *   damit Ratgeber-Beiträge nicht von Artikeln verdrängt werden (limit_scope=each).
 */
if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();

      this.input = this.querySelector('input[type="search"]');
      this.resultsContainer = this.querySelector('.js-results-container');
      this.btnSubmit = this.querySelector('.js-btn-submit');
      this.btnClearText = this.querySelector('.js-btn-clear-text');
      this.errorMessageContainer = this.querySelector('.js-message-error');
      this.status = this.querySelector('[data-rk-ps-status]');
      this.inline = this.hasAttribute('data-rk-inline');
      this.drawer = this.closest('.js-drawer');
      this.timeout = null;
      this.isOpen = false;
      this.requestId = 0;

      if (!this.input || !this.resultsContainer) return;

      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (event) => this.onInputKeydown(event));
      this.input.addEventListener('focus', () => {
        if (this.ohneOeffnen) return;
        if (this.inline && this.input.value.trim() && this.resultsContainer.children.length) this.showResults();
      });
      this.resultsContainer.addEventListener('keydown', (event) => this.onResultsKeydown(event));

      if (this.btnClearText) {
        this.btnClearText.addEventListener('click', (event) => {
          event.preventDefault();
          this.clear();
          this.resetDrawerLayout();
        });
      }

      if (this.inline) {
        document.addEventListener('click', (event) => {
          if (this.isOpen && !this.contains(event.target)) this.hideResults();
        });
        this.addEventListener('focusout', (event) => {
          if (event.relatedTarget && !this.contains(event.relatedTarget)) this.hideResults();
        });
        document.addEventListener('rk:suche-schliessen', () => this.hideResults());
        window.addEventListener('pageshow', () => this.hideResults());
      }
    }

    onInput() {
      clearTimeout(this.timeout);
      if (this.btnClearText) this.btnClearText.disabled = this.input.value.length === 0;

      this.timeout = setTimeout(() => {
        const searchTerm = this.input.value.trim();

        if (!searchTerm.length) {
          this.requestId++;
          this.hideResults();
          this.hideError();
          this.resultsContainer.innerHTML = '';
          this.setStatus('');
          this.resetDrawerLayout();
          return;
        }

        this.getSearchResults(searchTerm);
        this.expandDrawerLayout();
      }, 300);
    }

    async getSearchResults(searchTerm) {
      const requestId = ++this.requestId;
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url =
        `${root}search/suggest?q=${encodeURIComponent(searchTerm)}` +
        '&resources[type]=product,collection,query,article,page' +
        '&resources[limit]=6' +
        '&resources[limit_scope]=each' +
        '&resources[options][unavailable_products]=last' +
        '&resources[options][fields]=title,product_type,variants.title,vendor,variants.sku,variants.barcode' +
        '&section_id=predictive-search';

      try {
        const response = await fetch(url);
        if (requestId !== this.requestId) return;
        if (!response.ok) throw new Error(String(response.status));

        const text = await response.text();
        if (requestId !== this.requestId) return;

        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Antwort ohne Vorschläge');

        this.resultsContainer.innerHTML = section.innerHTML;
        this.showResults();
        this.announce();

        // Schnellkauf-Knöpfe in neu geladenen Karten anbinden (falls vorhanden)
        const quickCart = document.querySelector('quick-cart-drawer');
        if (quickCart && typeof quickCart.initTriggers === 'function') quickCart.initTriggers();
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.showError();
      }
    }

    /* ---------- Anzeigen / Verbergen ---------- */

    showResults() {
      this.isOpen = true;
      this.classList.add('is-open');
      this.resultsContainer.classList.remove('hidden');
      this.hideError();
      if (this.btnSubmit && !this.inline) this.btnSubmit.setAttribute('tabindex', '-1');
      if (this.btnClearText) this.btnClearText.disabled = false;
      if (this.inline) document.dispatchEvent(new CustomEvent('rk:suche-offen'));
    }

    hideResults() {
      this.isOpen = false;
      this.classList.remove('is-open');
      this.resultsContainer.classList.add('hidden');
      if (this.btnSubmit) this.btnSubmit.removeAttribute('tabindex');
    }

    clear() {
      this.requestId++;
      this.hideResults();
      this.hideError();
      this.input.value = '';
      this.resultsContainer.innerHTML = '';
      this.setStatus('');
      if (this.btnClearText) this.btnClearText.disabled = true;
      this.input.focus();
    }

    /* Theme-Kompatibilität: anderer Code ruft open()/close() auf */
    open() {
      this.showResults();
    }

    close() {
      this.clear();
    }

    showError() {
      this.hideResults();
      if (this.errorMessageContainer) this.errorMessageContainer.classList.remove('hidden');
      this.setStatus('Die Vorschläge konnten gerade nicht geladen werden.');
    }

    hideError() {
      if (this.errorMessageContainer) this.errorMessageContainer.classList.add('hidden');
    }

    announce() {
      const root = this.resultsContainer.querySelector('[data-rk-anzahl]');
      const count = root ? parseInt(root.getAttribute('data-rk-anzahl'), 10) || 0 : 0;
      if (!count) {
        this.setStatus('Keine Vorschläge gefunden.');
        return;
      }
      this.setStatus(
        (count === 1 ? '1 Vorschlag' : `${count} Vorschläge`) + ' – mit der Pfeiltaste nach unten erreichbar.'
      );
    }

    setStatus(text) {
      if (this.status) this.status.textContent = text;
    }

    /* ---------- Tastatur ---------- */

    /* Fokus zurück ins Feld, ohne dass der focus-Handler die Vorschläge wieder öffnet */
    fokusOhneOeffnen() {
      this.ohneOeffnen = true;
      this.input.focus();
      this.ohneOeffnen = false;
    }

    focusables() {
      return Array.from(this.resultsContainer.querySelectorAll('a[href], button:not([disabled])'));
    }

    onInputKeydown(event) {
      if (event.key === 'Escape') {
        if (this.isOpen) {
          event.preventDefault();
          event.stopPropagation();
          this.hideResults();
          return;
        }
        if (this.input.value) {
          event.preventDefault();
          event.stopPropagation();
          this.clear();
          this.resetDrawerLayout();
          return;
        }
        if (this.drawer) {
          const drawer = this.closest('search-drawer') || this.drawer.parentElement.parentElement;
          if (drawer && typeof drawer.toggleDrawer === 'function') drawer.toggleDrawer();
        }
        return;
      }

      if (event.key === 'ArrowDown' && this.isOpen) {
        const items = this.focusables();
        if (items.length) {
          event.preventDefault();
          items[0].focus();
        }
      }
    }

    onResultsKeydown(event) {
      const items = this.focusables();
      const index = items.indexOf(document.activeElement);

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        (items[index + 1] || items[0]).focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (index <= 0) {
          this.fokusOhneOeffnen();
        } else {
          items[index - 1].focus();
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.hideResults();
        this.fokusOhneOeffnen();
      }
    }

    /* ---------- Such-Drawer des Themes (Höhe, Vorschlagsliste) ---------- */

    isSmallScreen() {
      return typeof DeviceDetector !== 'undefined' && (DeviceDetector.isMobile() || DeviceDetector.isTablet());
    }

    expandDrawerLayout() {
      if (!this.drawer) return;
      const suggestions = this.drawer.querySelector('.search-drawer__suggestions');
      if (this.isSmallScreen()) this.drawer.style.height = '100%';
      if (suggestions) suggestions.style.display = 'none';
    }

    resetDrawerLayout() {
      if (!this.drawer) return;
      const suggestions = this.drawer.querySelector('.search-drawer__suggestions');
      this.drawer.style.height = 'auto';
      if (suggestions) suggestions.style.display = 'block';
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
